import * as moment from "moment";

import axios from "axios";
import { Appointment } from "@teamprevent/common/calendar";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";
import { OpenClinic, Clinic, FixedLengthClinic, NonSlotClinic } from "@teamprevent/common/calendar/models";

import { CalendarDetails } from "./models";
import { ClinicDetails } from "./clinics/models";

// We should try to improve the types associated with the Appointment Union so that we can do proper type narrowing here rather than casting
export const mapToAppointmentModel = (appointment: ServerModelWithDateStrings<Appointment>): Appointment => {
    if ("billableByType" in appointment) {
        const clinic: ServerModelWithDateStrings<Clinic> = appointment;
        if (clinic.clinicType === "Health Surveillance") {
            return {
                ...(clinic as ServerModelWithDateStrings<NonSlotClinic>),
                start: clinic.start ? moment.utc(clinic.start) : undefined,
                end: clinic.end ? moment.utc(clinic.end) : undefined,
                appointments: (clinic as ServerModelWithDateStrings<NonSlotClinic>).appointments
            } satisfies NonSlotClinic;
        }

        return {
            ...clinic,
            start: clinic.start ? moment.utc(clinic.start) : undefined,
            end: clinic.end ? moment.utc(clinic.end) : undefined,
            appointments: (clinic as ServerModelWithDateStrings<FixedLengthClinic | OpenClinic>).appointments.map(a => ({
                ...a,
                start: a.start ? moment.utc(a.start) : undefined,
                end: a.end ? moment.utc(a.end) : undefined,
            }))
        } satisfies Clinic;
    }
    return {
        ...appointment,
        start: appointment.start ? moment.utc(appointment.start) : undefined,
        end: appointment.end ? moment.utc(appointment.end) : undefined
    };
};

class CalendarApi {
    private readonly apiUrl = "/api/appointment";
    private getInDateRangeAbortController = new AbortController();

    public async getInDateRange(userIds: string[], roomIds: string[], startDate: moment.Moment, endDate: moment.Moment): Promise<CalendarDetails[]> {
        const startDateString = startDate.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const endDateString = endDate.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);

        const params = { startDate: startDateString, endDate: endDateString, roomIds };
        this.getInDateRangeAbortController.abort("Cancelled to allow subsequent request");
        this.getInDateRangeAbortController = new AbortController();

        return (await axios.post<
            ServerModelWithDateStrings<CalendarDetails>[]
        >(this.apiUrl, { userIds }, {
            params,
            signal: this.getInDateRangeAbortController.signal
        })).data.map(d => ({ ...d, appointments: d.appointments.map(mapToAppointmentModel) }));
    }

    public async get(id: string): Promise<Appointment> {
        return (await axios.get(`${this.apiUrl}/${id}`)).data as Appointment;
    }

    public async markAsArrived(id: string): Promise<ClinicDetails> {
        return (await axios.post(`${this.apiUrl}/${id}/MarkAsArrived`)).data as ClinicDetails;
    }

    public async deleteAppointment(
        appointmentId: string,
        clinicId?: string
    ): Promise<void> {
        if (clinicId) {
            await axios.delete(`${this.apiUrl}/${clinicId}/appointments/${appointmentId}`);
        } else {
            await axios.delete(`${this.apiUrl}/${appointmentId}`);
        }
    }
}

export const calendarApi = new CalendarApi();
